import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { Link } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import * as XLSX from 'xlsx';
import {
  Car,
  Search,
  Eye,
  Download,
  ChevronDown,
  ChevronUp,
  Users,
} from 'lucide-react';
import { api } from '../../utils/api';
import RideMatchModal from '../../components/Trips/RideMatchModal';
import usePersistentFilters from '../../hooks/usePersistentFilters';
import { cancelReasons } from '../../components/driverCancelReasons';

// TripLocationViewer component - moved outside to prevent recreation
const TripLocationViewer = React.memo(({ startLocation, endLocation, tripId }) => {
  const [showLocations, setShowLocations] = useState(false);
  const [startLocationName, setStartLocationName] = useState(null);
  const [endLocationName, setEndLocationName] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const getLocationName = async (location, type = '') => {

    // Handle both array format [longitude, latitude] and object format {latitude, longitude}
    let latitude, longitude;

    if (Array.isArray(location) && location.length >= 2) {
      // Array format: [longitude, latitude] (MongoDB/GeoJSON standard)
      longitude = location[0];
      latitude = location[1];
    } else if (location && typeof location === 'object') {
      // Object format: {latitude, longitude}
      latitude = location.latitude;
      longitude = location.longitude;
    } else {
      console.warn('Invalid location format:', location);
      return null;
    }

    if (!latitude || !longitude) {
      console.warn('Missing latitude or longitude:', { latitude, longitude });
      return null;
    }

    try {
      // Use api utility which includes authentication
      const response = await api.post('/geocoding/reverse', {
        latitude: latitude,
        longitude: longitude,
      });

      const placeName = response?.placeName || response?.data?.placeName || 'Location not found';
      console.log('Final place name for', type, ':', placeName);
      return placeName;

    } catch (error) {
      console.error('Error in getLocationName for', type, ':', error);
      const fallback = `${latitude.toFixed(4)}, ${longitude.toFixed(4)}`;
      console.log('Using fallback location for', type, ':', fallback);
      return fallback;
    }
  };

  const handleViewLocations = async () => {
    // If we already have the data, toggle dialog
    if (startLocationName && endLocationName) {
      console.log('Using cached data, toggling dialog');
      setShowLocations(prev => !prev);
      return;
    }

    if (showLocations) {
      // Close dialog
      console.log('Closing dialog');
      setShowLocations(false);
      return;
    }

    // Start loading
    setIsLoading(true);

    try {
      // Fetch both locations
      const promises = [];

      if (!startLocationName && startLocation) {
        promises.push(
          getLocationName(startLocation).then(name => {
            console.log('Start location fetched:', name);
            return { type: 'start', name };
          })
        );
      }
      if (!endLocationName && endLocation) {
        promises.push(
          getLocationName(endLocation).then(name => {
            console.log('End location fetched:', name);
            return { type: 'end', name };
          })
        );
      }

      if (promises.length > 0) {
        console.log('Making API calls...');
        const results = await Promise.all(promises);

        console.log('API results received:', results);

        // Process all results first
        let newStartName = startLocationName;
        let newEndName = endLocationName;

        results.forEach(result => {
          if (result.type === 'start') {
            newStartName = result.name;
          } else if (result.type === 'end') {
            newEndName = result.name;
          }
        });

        console.log('Updating states with:', { newStartName, newEndName });

        // Update location names first
        setStartLocationName(newStartName);
        setEndLocationName(newEndName);
        setIsLoading(false);

        // Use setTimeout to ensure state updates are processed before showing modal
        setTimeout(() => {
          console.log('Opening modal after state update. Current state:', {
            startLocationName: newStartName,
            endLocationName: newEndName
          });
          setShowLocations(true);
        }, 100);

      } else {
        setShowLocations(true);
        setIsLoading(false);
      }

    } catch (error) {
      console.error('Error fetching location names:', error);
      setIsLoading(false);
      // Still show the dialog even if geocoding fails, but with timeout
      setTimeout(() => {
        console.log('Opening modal after error, showing coordinates only');
        setShowLocations(true);
      }, 100);
    }
  };

  const formatLocation = (location) => {
    // Handle both array format [longitude, latitude] and object format {latitude, longitude}
    let latitude, longitude;

    if (Array.isArray(location) && location.length >= 2) {
      // Array format: [longitude, latitude] (MongoDB/GeoJSON standard)
      longitude = location[0];
      latitude = location[1];
    } else if (location && typeof location === 'object') {
      // Object format: {latitude, longitude}
      latitude = location.latitude;
      longitude = location.longitude;
    } else {
      return 'Location not available';
    }

    if (!latitude || !longitude) {
      return 'Location not available';
    }

    return `${latitude.toFixed(4)}, ${longitude.toFixed(4)}`;
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <div className="space-y-1 text-sm">
          <div className="flex items-center text-gray-600">
            <span className="h-2 w-2 bg-green-500 rounded-full mr-2"></span>
            <span className="font-mono text-xs">{formatLocation(startLocation)}</span>
          </div>
          <div className="flex items-center text-gray-600">
            <span className="h-2 w-2 bg-red-500 rounded-full mr-2"></span>
            <span className="font-mono text-xs">{formatLocation(endLocation)}</span>
          </div>
        </div>
        <button
          onClick={handleViewLocations}
          disabled={isLoading}
          className={`px-3 py-1 text-xs rounded-md transition-colors ${isLoading
            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
            : 'bg-blue-50 text-blue-600 hover:bg-blue-100'
            }`}
        >
          {isLoading ? (
            <div className="flex items-center space-x-1">
              <div className="w-3 h-3 border border-blue-300 border-t-blue-600 rounded-full animate-spin"></div>
              <span>Loading...</span>
            </div>
          ) : (
            <Eye className="h-3 w-3" />
          )}
        </button>
      </div>

      {/* Location Names Dialog */}
      {showLocations && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 relative">
            <button
              onClick={() => setShowLocations(false)}
              className="absolute top-2 right-2 text-gray-400 hover:text-gray-600"
            >
              ✕
            </button>

            <h3 className="text-lg font-semibold mb-4 text-gray-900">Trip Locations</h3>

            <div className="space-y-4">
              <div className="border-l-4 border-green-500 pl-4">
                <div className="flex items-center mb-1">
                  <span className="h-3 w-3 bg-green-500 rounded-full mr-2"></span>
                  <span className="text-sm font-medium text-gray-700">Start Location</span>
                </div>
                <p className="text-sm text-gray-900 font-medium">
                  {startLocationName || 'Loading...'}
                </p>
                <p className="text-xs text-gray-500 font-mono">
                  {formatLocation(startLocation)}
                </p>
              </div>

              <div className="border-l-4 border-red-500 pl-4">
                <div className="flex items-center mb-1">
                  <span className="h-3 w-3 bg-red-500 rounded-full mr-2"></span>
                  <span className="text-sm font-medium text-gray-700">End Location</span>
                </div>
                <p className="text-sm text-gray-900 font-medium">
                  {endLocationName || 'Loading...'}
                </p>
                <p className="text-xs text-gray-500 font-mono">
                  {formatLocation(endLocation)}
                </p>
              </div>
            </div>

            <div className="mt-6 flex justify-end">
              <button
                onClick={() => setShowLocations(false)}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

const Trips = () => {
  const [rideMatchTrip, setRideMatchTrip] = useState(null);
  const [period, setPeriod] = useState('');
  const [regionalOffices, setRegionalOffices] = useState([]);
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState(new Set());

  // Individual stats filters
  const [completedRidesPeriod, setCompletedRidesPeriod] = useState('thisMonth');
  const [totalRidesPeriod, setTotalRidesPeriod] = useState('thisMonth');

  // Individual status filters
  const [todayRidesStatus, setTodayRidesStatus] = useState('');
  const [yesterdayRidesStatus, setYesterdayRidesStatus] = useState('');
  const [completedRidesStatus,] = useState('COMPLETED');
  const [totalRidesStatus, setTotalRidesStatus] = useState('');

  const defaultFilters = {
    search: '',
    status: '',
    paymentMethod: '',
    vehicleType: '',
    regionalOffice: '',
    paymentStatus: '',
    femaleOnly: false,
    driverType: '',
    developerOnly: 'Exclude',
    startDate: '',
    endDate: '',
    groupTrips: false,
    page: 1,
    limit: 10,
    verificationNeeded: false,
    showCancelReason: false,
  };

  const {
    filters,
    handleFilterChange: originalHandleFilterChange,
    handlePageChange,
    clearFilters,
    updateFilters
  } = usePersistentFilters(defaultFilters, 'trips-filters');

  // Normal trips only - acting driver trips live on the Acting Trip page
  const effectiveFilters = { ...filters, isActingDriverTrip: false };

  // Use original handleFilterChange
  const handleFilterChange = (key, value) => {
    // If status is being changed to COMPLETED, disable grouping
    if (key === 'status' && value === 'COMPLETED') {
      originalHandleFilterChange('groupTrips', false);
    }
    originalHandleFilterChange(key, value);
  };

  // Toggle group expansion function
  const toggleGroupExpansion = (tripId) => {
    setExpandedGroups(prev => {
      const newSet = new Set(prev);
      if (newSet.has(tripId)) {
        newSet.delete(tripId);
      } else {
        newSet.add(tripId);
      }
      return newSet;
    });
  };

  // Helper function for period options
  const periodOptions = [
    { value: '', label: 'All Data' },
    { value: 'today', label: 'Today' },
    { value: 'yesterday', label: 'Yesterday' },
    { value: 'thisWeek', label: 'This Week' },
    { value: 'pastWeek', label: 'Past Week' },
    { value: 'thisMonth', label: 'This Month' },
    { value: 'lastMonth', label: 'Last Month' }
  ];

  // Status filter options
  const statusOptions = [
    { value: '', label: 'All Status' },
    { value: 'COMPLETED', label: 'Completed' },
    { value: 'PENDING', label: 'Waiting for Driver' },
    { value: 'ASSIGNED', label: 'Assigned' },
    // { value: 'STARTED', label: 'Started' },
    { value: 'IN_PROGRESS', label: 'In Progress' },
    { value: 'CANCELLED', label: 'Cancelled (All)' },
    { value: 'CANCELLED_BEFORE_PICKUP', label: 'Cancelled - Before Pickup' },
    { value: 'CANCELLED_AFTER_PICKUP', label: 'Cancelled - After Pickup' },
    { value: 'DROPPED', label: 'Dropped' },
    { value: 'PICKEDUP', label: 'Picked Up' },
    { value: 'ACCEPTED', label: 'Accepted' },
    { value: 'failed', label: 'Driver NOT Accepted' }
  ];

  // Query for efficient dashboard stats with individual periods and status filters
  const { data: statsData, isLoading: statsLoading } = useQuery(
    ['trips-stats', completedRidesPeriod, totalRidesPeriod, todayRidesStatus, yesterdayRidesStatus, completedRidesStatus, totalRidesStatus, filters.developerOnly, 'normal'],
    () => api.get('/trips/stats', {
      params: {
        completedRidesPeriod,
        totalRidesPeriod,
        todayRidesStatus,
        yesterdayRidesStatus,
        completedRidesStatus,
        totalRidesStatus,
        developerOnly: filters.developerOnly,
        isActingDriverTrip: false
      }
    }),
  {
    keepPreviousData: true,
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: false
  }
  );

  const { data, isLoading, isFetching, error } = useQuery(
    ['trips', effectiveFilters],
    () => {
      return api.get('/trips', { params: { ...effectiveFilters, groupTrips: filters.groupTrips.toString() } });
    },
    {
      keepPreviousData: true,
      staleTime: 0,
      refetchOnMount: 'always',
      refetchOnWindowFocus: false
    }
  );

  if (error) {
    toast.error('Failed to load trips');
  }

  // Fetch regional offices on component mount
  React.useEffect(() => {
    const fetchRegionalOffices = async () => {
      try {
        const response = await api.get('/regional-offices', {
          params: { limit: 100, isActive: true } // Get up to 100 active offices
        });
        if (response.data.success) {
          setRegionalOffices(response.data.data.offices || []);
        }
      } catch (error) {
        // Handle error silently
      }
    };

    fetchRegionalOffices();
  }, []);

  // Clear group filter if status is COMPLETED
  React.useEffect(() => {
    if ((filters.status === 'COMPLETED' || filters.status === 'FINISHED') && filters.groupTrips) {
      handleFilterChange('groupTrips', false);
    }
  }, [filters.status, filters.groupTrips]);

  // Helper function to check if filter is active and get CSS classes
  const getFilterClasses = (filterValue, isCheckbox = false) => {
    // For developer filter, consider 'Exclude' as inactive (default state)
    const isActive = isCheckbox
      ? filterValue === true
      : (filterValue && filterValue !== '' && filterValue !== 'Exclude' && filterValue !== 'Include');
    const baseClasses = isCheckbox
      ? "h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
      : "input";

    if (isActive) {
      return isCheckbox
        ? `${baseClasses} ring-2 ring-blue-400 bg-blue-50`
        : `${baseClasses} border-blue-500 bg-blue-50 shadow-md ring-1 ring-blue-200`;
    }

    return baseClasses;
  };

  const pagination = data?.data?.data?.pagination || {};

  // Apply frontend filtering for femaleOnly
  const trips = React.useMemo(() => {
    let allTrips = data?.data?.data?.trips || [];

    // Apply female-only filter if needed
    if (filters.femaleOnly) {
      allTrips = allTrips.filter(trip => {
        // Primary check: trip.femaleOnly should be true
        if (trip.femaleOnly === true) {
          return true;
        }

        // Secondary checks for passenger gender if available
        const passengerGender = trip.passengerGender || trip.passengerId?.gender;
        if (passengerGender === 'FEMALE' || passengerGender === 'female' || passengerGender === 'F') {
          return true;
        }

        // Check passenger name for female indicators
        const passengerName = trip.bookingForName || '';
        const hasFemaleName = passengerName.toLowerCase().includes('ms.') ||
          passengerName.toLowerCase().includes('mrs.') ||
          passengerName.toLowerCase().includes('miss');

        return hasFemaleName;
      });
    }


    return allTrips;
  }, [data?.data?.data?.trips, filters.femaleOnly]);

  // Use stats from efficient backend endpoint
  const stats = React.useMemo(() => {
    if (statsData?.data?.data) {
      return {
        todayRides: statsData.data.data.todayRides || 0,
        yesterdayRides: statsData.data.data.yesterdayRides || 0,
        completedRides: statsData.data.data.completedRides || 0,
        totalRides: statsData.data.data.totalRides || 0
      };
    }

    // Fallback to zeros while loading
    return {
      todayRides: 0,
      yesterdayRides: 0,
      completedRides: 0,
      totalRides: 0
    };
  }, [statsData?.data?.data]);

  // Handle period change
  const handlePeriodChange = (newPeriod) => {
    try {
      setPeriod(newPeriod);

      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

      let startDate, endDate;

      switch (newPeriod) {
        case 'today':
          startDate = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 0, 0, 0, 0);
          endDate = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59, 999);
          break;
        case 'yesterday':
          const yesterday = new Date(today);
          yesterday.setDate(yesterday.getDate() - 1);
          startDate = new Date(yesterday.getFullYear(), yesterday.getMonth(), yesterday.getDate(), 0, 0, 0, 0);
          endDate = new Date(yesterday.getFullYear(), yesterday.getMonth(), yesterday.getDate(), 23, 59, 59, 999);
          break;
        case 'thisWeek':
          const dayOfWeek = today.getDay(); // 0 = Sunday, 1 = Monday, etc.
          const weekStart = new Date(today);
          weekStart.setDate(today.getDate() - dayOfWeek);
          startDate = new Date(weekStart.getFullYear(), weekStart.getMonth(), weekStart.getDate(), 0, 0, 0, 0);
          endDate = new Date(now); // Current time
          break;
        case 'pastWeek':
          // Get last week
          const currentDayOfWeek = today.getDay(); // 0 = Sunday, 1 = Monday, etc.
          const daysToLastSunday = currentDayOfWeek + 7; // Days back to last Sunday
          const lastSunday = new Date(today);
          lastSunday.setDate(today.getDate() - daysToLastSunday);
          const lastSaturday = new Date(lastSunday);
          lastSaturday.setDate(lastSunday.getDate() + 6);
          startDate = new Date(lastSunday.getFullYear(), lastSunday.getMonth(), lastSunday.getDate(), 0, 0, 0, 0);
          endDate = new Date(lastSaturday.getFullYear(), lastSaturday.getMonth(), lastSaturday.getDate(), 23, 59, 59, 999);
          break;
        case 'thisMonth':
          startDate = new Date(today.getFullYear(), today.getMonth(), 1, 0, 0, 0, 0); // First day of month
          endDate = new Date(now); // Current time
          break;
        default:
          const defaultStart = new Date(today);
          defaultStart.setDate(today.getDate() - 7);
          startDate = new Date(defaultStart.getFullYear(), defaultStart.getMonth(), defaultStart.getDate(), 0, 0, 0, 0);
          endDate = new Date(now);
      }

      // Validate dates
      if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
        return;
      }

      // Ensure end date is not before start date
      if (endDate < startDate) {
        return;
      }

      // Format dates as YYYY-MM-DDTHH:mm:ss in local time (without timezone)
      const formatLocalDateTime = (date) => {
        const localDate = new Date(date);

        const timezoneOffset = localDate.getTimezoneOffset() * 60000; // in milliseconds
        const localISOTime = new Date(localDate.getTime() - timezoneOffset);
        return localISOTime.toISOString().slice(0, 19);
      };

      const startDateStr = formatLocalDateTime(startDate);
      const endDateStr = formatLocalDateTime(endDate);

      // Update both dates simultaneously to avoid timing issues
      updateFilters({
        ...filters,
        startDate: startDateStr,
        endDate: endDateStr,
        page: 1 // Reset to first page when filters change
      });
    } catch (error) {
      // Handle error silently
    }
  }; 
  
  const getStatusBadge = (trip) => {
    const status = trip.status;

    const statusConfig = {
      'PENDING': { class: 'badge-warning', text: 'Waiting for Driver' },
      'ASSIGNED': { class: 'badge-info', text: 'Assigned' },
      'STARTED': { class: 'badge-primary border', text: 'Started' },
      'IN_PROGRESS': { class: 'badge-primary', text: 'In Progress' },
      'COMPLETED': { class: 'badge-success', text: 'Completed' },
      'CANCELLED': { class: 'badge-danger', text: 'Cancelled' },
      'DROPPED': { class: 'badge-danger', text: 'Dropped' },
      'CANCELLED_BEFORE_PICKUP': { class: 'badge-danger', text: 'Cancelled - Before Pickup' },
      'CANCELLED_AFTER_PICKUP': { class: 'badge-danger', text: 'Cancelled - After Pickup' },
      'failed': { class: 'badge-info', text: 'Driver NOT Accepted' }
    };

    // Handle dynamic cancelled status based on canceledBefore field
    if (status && status.includes('CANCELLED') && trip.canceledBefore !== undefined) {
      const text = trip.canceledBefore ? 'Cancelled - Before Pickup' : 'Cancelled - After Pickup';
      return <span className="badge badge-danger">{text}</span>;
    }

    const config = statusConfig[status] || { class: 'badge-info', text: status };
    return <span className={`badge ${config.class}`}>{config.text}</span>;
  };

  const getPaymentMethodBadge = (method) => {
    const methodConfig = {
      'CASH': { class: 'badge-success', text: 'Cash' },
      'CARD': { class: 'badge-primary', text: 'Card' },
      'UPI': { class: 'badge-info', text: 'UPI' },
      'WALLET': { class: 'badge-warning', text: 'Wallet' }
    };

    const config = methodConfig[method] || { class: 'badge-info', text: method };
    return <span className={`badge ${config.class}`}>{config.text}</span>;
  };

  const calculateFare = (trip) => {
    const baseFare = trip.minFare || 0;
    const adjustment = trip.fareAdjustment || 0;
    return baseFare + adjustment;
  };

  // Determine if a trip requires verification using several possible fields
  const tripNeedsVerification = (trip) => {
    if (!trip) return false;
    if (trip.verificationNeeded === true || trip.verificationNeeded === 'true') return true;
    if (trip.verificationRequired === true || trip.verificationRequired === 'true') return true;
    if (trip.needsVerification === true || trip.needsVerification === 'true') return true;
    if (trip.verificationStatus && String(trip.verificationStatus).toLowerCase() === 'pending') return true;
    if (trip.otpVerified === false || trip.isOtpVerified === false) return true;
    return false;
  };

  // Export functionality
  const handleExportToExcel = async () => {
    try {
      toast.loading('Preparing export...', { id: 'export' });

      // Get all filtered data for export (remove pagination limit but keep filters)
      const exportParams = {
        ...effectiveFilters,
        limit: 10000, // Get all filtered records
        page: 1
      };

      const exportResponse = await api.get('/trips', { params: exportParams });
      const exportData = exportResponse?.data?.data?.trips || [];

      if (exportData.length === 0) {
        toast.error('No data available for export with current filters', { id: 'export' });
        return;
      }

      toast.loading('Fetching driver details...', { id: 'export' });

      // Get unique driver IDs for batch lookup
      const driverIds = [...new Set(exportData.map(trip => trip.driverId?._id || trip.driverId).filter(Boolean))];

      let driversMap = {};

      // Fetch driver details using the ids parameter if we have driver IDs
      if (driverIds.length > 0) {
        try {
          const driversResponse = await api.get('/drivers', {
            params: {
              ids: driverIds.join(','),
              limit: 1000
            }
          });

          if (driversResponse?.data?.success) {
            const drivers = driversResponse.data.data.drivers || [];
            drivers.forEach(driver => {
              driversMap[driver._id] = driver.name || 'Unknown Driver';
            });
          }
        } catch (error) {
          console.warn('Failed to fetch drivers:', error);
          // Continue with export even if driver fetch fails
        }
      }

      toast.loading('Generating Excel file...', { id: 'export' });

      // Helper function to format location coordinates
      const formatLocation = (location) => {
        let latitude, longitude;

        if (Array.isArray(location) && location.length >= 2) {
          longitude = location[0];
          latitude = location[1];
        } else if (location && typeof location === 'object') {
          latitude = location.latitude;
          longitude = location.longitude;
        } else {
          return 'Location not available';
        }

        if (!latitude || !longitude) {
          return 'Location not available';
        }

        return `${latitude.toFixed(4)}, ${longitude.toFixed(4)}`;
      };

      // Prepare data for Excel export
      const excelData = exportData.map((trip, index) => {
        // Get driver name
        const driverName = trip.driverId?.name || driversMap[trip.driverId?._id || trip.driverId] || 'Not Assigned';

        // Calculate fare
        const fare = trip.actualFare || (trip.minFare || 0) + (trip.fareAdjustment || 0);

        // Format status
        let status = trip.status;
        if (status && status.includes('CANCELLED') && trip.canceledBefore !== undefined) {
          status = trip.canceledBefore ? 'Cancelled - Before Pickup' : 'Cancelled - After Pickup';
        }

        return {
          'S.No': index + 1,
          'Trip ID': trip._id || 'N/A',
          'Passenger Name': trip.bookingForName || 'N/A',
          'Passenger Phone': trip.bookingForPhone || 'N/A',
          'Driver Name': driverName,
          'Driver ID': trip.driverId?._id || trip.driverId || 'N/A',
          'Vehicle Type': trip.vehicleType || 'N/A',
          'Start Location': formatLocation(trip.startLocation),
          'End Location': formatLocation(trip.endLocation),
          'Status': status || 'N/A',
          'Payment Method': trip.paymentMethod || 'N/A',
          'Payment Status': trip.paymentStatus || 'N/A',
          'Fare (₹)': fare.toFixed(2),
          'Distance (km)': trip.finalDistance ? (Math.floor(trip.finalDistance * 10) / 10).toString() : 'N/A',
          'OTP': trip.otp || 'N/A',
          'Female Only': trip.femaleOnly ? 'Yes' : 'No',
          'Booking Time': trip.bookingTime ? new Date(trip.bookingTime).toLocaleString() : 'N/A',
          'Start Time': trip.startTime ? new Date(trip.startTime).toLocaleString() : 'N/A',
          'End Time': trip.endTime ? new Date(trip.endTime).toLocaleString() : 'N/A',
          'Created Date': trip.createdAt ? new Date(trip.createdAt).toLocaleDateString() : 'N/A',
          'Booking App Version': trip.appVersion || 'N/A',
          'Booking Build Number': trip.buildNumber || 'N/A'
        };
      });

      // Create workbook and worksheet
      const workbook = XLSX.utils.book_new();
      const worksheet = XLSX.utils.json_to_sheet(excelData);

      // Set column widths
      const colWidths = [
        { wch: 8 },  // S.No
        { wch: 20 }, // Trip ID
        { wch: 20 }, // Passenger Name
        { wch: 15 }, // Passenger Phone
        { wch: 20 }, // Driver Name
        { wch: 20 }, // Driver ID
        { wch: 12 }, // Vehicle Type
        { wch: 25 }, // Start Location
        { wch: 25 }, // End Location
        { wch: 15 }, // Status
        { wch: 12 }, // Payment Method
        { wch: 15 }, // Payment Status
        { wch: 12 }, // Fare
        { wch: 12 }, // Distance
        { wch: 8 },  // OTP
        { wch: 12 }, // Female Only
        { wch: 20 }, // Booking Time
        { wch: 20 }, // Start Time
        { wch: 20 }, // End Time
        { wch: 15 }  // Created Date
      ];
      worksheet['!cols'] = colWidths;

      // Add worksheet to workbook
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Trips Report');

      // Generate filename with current date and filter info
      const currentDate = new Date().toISOString().split('T')[0];
      let filename = `Trips_Report_${currentDate}`;

      // Add filter info to filename if filters are applied
      if (filters.search || filters.status || filters.paymentMethod || filters.vehicleType ||
        filters.regionalOffice || filters.paymentStatus || filters.femaleOnly ||
        filters.driverType || filters.developerOnly === 'Only' || filters.startDate || filters.endDate) {
        filename += '_filtered';
      }
      filename += '.xlsx';

      // Download the file
      XLSX.writeFile(workbook, filename);

      toast.success(`Exported ${excelData.length} filtered records successfully!`, { id: 'export' });

    } catch (error) {
      console.error('Export failed:', error);
      toast.error('Failed to export data. Please try again.', { id: 'export' });
    }
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="page-header">
        <div className="px-4 py-5 sm:px-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="page-title">Normal Trips</h1>
              <p className="page-subtitle">
                Monitor and manage normal ride trips. Acting driver trips are on the Acting Trips page.
              </p>
            </div>
            <Link to="/trips/active?type=normal" className="btn btn-primary">
              <Car className="h-4 w-4 mr-2" />
              View Active Trips
            </Link>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Today Rides Card */}
        <div className="card">
          <div className="card-body p-4">
            <div className="flex flex-wrap items-start justify-between gap-2 mb-2 min-h-[48px]">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center">
                    <Car className="h-4 w-4 text-white" />
                  </div>
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-600">Today Rides</p>
                </div>
              </div>
              <div className="flex flex-col gap-1 w-full sm:w-auto">
                <select
                  value={todayRidesStatus}
                  onChange={(e) => setTodayRidesStatus(e.target.value)}
                  className="text-xs border border-gray-200 rounded px-2 py-1 bg-white w-full sm:w-32 flex-shrink-0"
                >
                  {statusOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <p className="text-2xl font-bold text-gray-900">
              {isLoading || statsLoading ? '...' : (stats.todayRides || 0).toLocaleString()}
            </p>
          </div>
        </div>

        {/* Yesterday Rides Card */}
        <div className="card">
          <div className="card-body p-4">
            <div className="flex flex-wrap items-start justify-between gap-2 mb-2 min-h-[48px]">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center">
                    <Car className="h-4 w-4 text-white" />
                  </div>
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-600">Yesterday Rides</p>
                </div>
              </div>
              <div className="flex flex-col gap-1 w-full sm:w-auto">
                <select
                  value={yesterdayRidesStatus}
                  onChange={(e) => setYesterdayRidesStatus(e.target.value)}
                  className="text-xs border border-gray-200 rounded px-2 py-1 bg-white w-full sm:w-32 flex-shrink-0"
                >
                  {statusOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <p className="text-2xl font-bold text-gray-900">
              {isLoading || statsLoading ? '...' : (stats.yesterdayRides || 0).toLocaleString()}
            </p>
          </div>
        </div>

        {/* Completed Rides Card */}
        <div className="card">
          <div className="card-body p-4">
            <div className="flex flex-wrap items-start justify-between gap-2 mb-2 min-h-[48px]">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-emerald-500 rounded-lg flex items-center justify-center">
                    <Car className="h-4 w-4 text-white" />
                  </div>
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-600">Completed Rides</p>
                </div>
              </div>
              <div className="flex flex-col gap-1 w-full sm:w-auto">
                <select
                  value={completedRidesPeriod}
                  onChange={(e) => setCompletedRidesPeriod(e.target.value)}
                  className="text-xs border border-gray-200 rounded px-2 py-1 bg-white w-full sm:w-32 flex-shrink-0"
                >
                  {periodOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <p className="text-2xl font-bold text-gray-900">
              {isLoading || statsLoading ? '...' : (stats.completedRides || 0).toLocaleString()}
            </p>
          </div>
        </div>

        {/* Total Rides Card */}
        <div className="card">
          <div className="card-body p-4">
            <div className="flex flex-wrap items-start justify-between gap-2 mb-2 min-h-[48px]">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-purple-500 rounded-lg flex items-center justify-center">
                    <Car className="h-4 w-4 text-white" />
                  </div>
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-600">Total Rides</p>
                </div>
              </div>
              <div className="flex flex-col gap-1 w-full sm:w-auto">
                <select
                  value={totalRidesPeriod}
                  onChange={(e) => setTotalRidesPeriod(e.target.value)}
                  className="text-xs border border-gray-200 rounded px-2 py-1 bg-white w-full sm:w-32 flex-shrink-0"
                >
                  {periodOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <select
                  value={totalRidesStatus}
                  onChange={(e) => setTotalRidesStatus(e.target.value)}
                  className="text-xs border border-gray-200 rounded px-2 py-1 bg-white w-full sm:w-32 flex-shrink-0"
                >
                  {statusOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <p className="text-2xl font-bold text-gray-900">
              {isLoading || statsLoading ? '...' : (stats.totalRides || 0).toLocaleString()}
            </p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="card mb-6 relative">
        <div className="card-header">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                onClick={() => setFiltersExpanded(!filtersExpanded)}
                className="flex items-center gap-2 text-lg font-medium text-gray-900 hover:text-blue-600 transition-colors"
              >
                <h3 className="card-title">Filters</h3>
                {filtersExpanded ? (
                  <ChevronUp className="h-4 w-4" />
                ) : (
                  <ChevronDown className="h-4 w-4" />
                )}
              </button>
              {(() => {
                const activeCount = Object.entries(filters).filter(([key, value]) => {
                  if (key === 'page' || key === 'limit') return false;
                  if (typeof value === 'boolean') return value === true;
                  // For developer filter, only count 'Only' as active
                  if (key === 'developerOnly') return value === 'Only';
                  return value && value !== '';
                }).length;

                return activeCount > 0 ? (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    {activeCount} active
                  </span>
                ) : null;
              })()}
            </div>
            <button
              onClick={() => {
                localStorage.removeItem('trips-filters');
                clearFilters();
                setPeriod(''); // Also clear period state
              }}
              className="btn btn-outline btn-sm"
            >
              Clear Filters
            </button>
          </div>
        </div>
        <div className={`card-body relative transition-all duration-500 ease-in-out ${filtersExpanded ? 'pb-16' : 'pb-4'}`}>

          {/* Row 1: Search, Status, Vehicle Type, Regional Office */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4 [&>*]:min-w-0">
            {/* Search */}
            <div>
              <label className="form-label">Search</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by passenger name, phone, driver name, trip ID, driver ID, OTP..."
                  value={filters.search}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                  className={`${getFilterClasses(filters.search)} pl-10`}
                />
              </div>
            </div>

            {/* Status Filter */}
            <div>
              <label className="form-label">Status</label>
              <select
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
                className={getFilterClasses(filters.status)}
              >
                <option value="">All Status</option>
                <option value="COMPLETED">Completed</option>
                <option value="DIVERGED">Diverged</option>
                <option value="FINISHED">Completed And Diverged</option>
                {/* <option value="STARTED">Started</option> */}
                <option value="PICKEDUP">Picked up</option>
                <option value="ACCEPTED">Accepted</option>
                <option value="DROPPED">Dropped</option>
                <option value="PENDING">Waiting for Driver</option>
                <option value="failed">Driver NOT Accepted</option>
                <option value="CANCELLED">Cancelled (All)</option>
                <option value="CANCELLED_BEFORE_PICKUP">Cancelled - Before Pickup</option>
                <option value="CANCELLED_AFTER_PICKUP">Cancelled - After Pickup</option>
              </select>
            </div>

            {/* Vehicle Type */}
            <div>
              <label className="form-label">Vehicle Type</label>
              <select
                value={filters.vehicleType}
                onChange={(e) => handleFilterChange('vehicleType', e.target.value)}
                className={getFilterClasses(filters.vehicleType)}
              >
                <option value="">All Types</option>
                <option value="HATCHBACK">Hatchback</option>
                <option value="SEDAN">Sedan</option>
                <option value="SUV">SUV</option>
                <option value="MUV">MUV</option>
                <option value="LUXURY">Luxury</option>
                <option value="BIKE">Bike</option>
              </select>
            </div>

            {/* Regional Office */}
            <div>
              <label className="form-label">Regional Office</label>
              <select
                value={filters.regionalOffice}
                onChange={(e) => handleFilterChange('regionalOffice', e.target.value)}
                className={getFilterClasses(filters.regionalOffice)}
              >
                <option value="">All Offices</option>
                {regionalOffices.map((office) => (
                  <option key={office._id} value={office._id}>
                    {office.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Row 2: Payment Type, Payment Status, Female Only, Driver Type */}
          <div className={`overflow-hidden transition-all duration-500 ease-in-out ${filtersExpanded
            ? 'max-h-96'
            : 'max-h-0'
            }`}>
            <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-4 [&>*]:min-w-0 transition-opacity ease-in-out ${filtersExpanded
              ? 'opacity-100 delay-0'
              : 'opacity-0 delay-500'
              }`}>
              {/* Payment Method */}
              <div>
                <label className="form-label">Payment Type</label>
                <select
                  value={filters.paymentMethod}
                  onChange={(e) => handleFilterChange('paymentMethod', e.target.value)}
                  className={getFilterClasses(filters.paymentMethod)}
                >
                  <option value="">All Methods</option>
                  <option value="CASH">Cash</option>
                  <option value="UPI">UPI</option>
                  <option value="ONLINE">Online</option>
                </select>
              </div>

              {/* Payment Status */}
              <div>
                <label className="form-label">Payment Status</label>
                <select
                  value={filters.paymentStatus}
                  onChange={(e) => handleFilterChange('paymentStatus', e.target.value)}
                  className={getFilterClasses(filters.paymentStatus)}
                >
                  <option value="">All Status</option>
                  <option value="COMPLETED">Payment Completed</option>
                  <option value="NOT_COMPLETED">Payment Pending/Failed</option>
                </select>
              </div>

              {/* Driver Type */}
              <div>
                <label className="form-label">Driver Type</label>
                <select
                  value={filters.driverType}
                  onChange={(e) => handleFilterChange('driverType', e.target.value)}
                  className={getFilterClasses(filters.driverType)}
                >
                  <option value="">All Types</option>
                  <option value="OWNER">Owner Driver</option>
                  <option value="VENDOR">Vendor</option>
                  <option value="ACTING_DRIVER">Acting Driver</option>
                </select>
              </div>

              <div>
                <label className="form-label">Developer Filter</label>
                <select
                  value={filters.developerOnly}
                  onChange={(e) => handleFilterChange('developerOnly', e.target.value)}
                  className={getFilterClasses(filters.developerOnly)}
                >
                  <option value="Exclude">Exclude Developers</option>
                  <option value="Include">Include Developers</option>
                  <option value="Only">Developers Only</option>
                </select>
              </div>

              {/* Female Only Checkbox */}
              <div>
                <label className="form-label">Gender Filter</label>
                <div className="flex items-center mt-2">
                  <input
                    type="checkbox"
                    id="femaleOnly"
                    checked={filters.femaleOnly}
                    onChange={(e) => handleFilterChange('femaleOnly', e.target.checked)}
                    className={getFilterClasses(filters.femaleOnly, true)}
                  />
                  <label htmlFor="femaleOnly" className="ml-2 text-sm font-medium text-gray-700">
                    Female Only
                  </label>
                </div>
              </div>
            </div>

            {/* Row 3: Start Date, End Date, Time Period */}
            <div className={`overflow-hidden transition-all duration-500 ease-in-out ${filtersExpanded
              ? 'max-h-96'
              : 'max-h-0'
              }`}>
              <div className={`grid grid-cols-1 md:grid-cols-3 gap-4 [&>*]:min-w-0 transition-opacity duration-300 ease-in-out ${filtersExpanded
                ? 'opacity-100 delay-0'
                : 'opacity-0 delay-500'
                }`}>
                {/* Start Date */}
                <div>
                  <label className="form-label">Start Date</label>
                  <input
                    type="date"
                    value={filters.startDate ? filters.startDate.split('T')[0] : ''}
                    onChange={(e) => {
                      // Clear period selection when manually changing dates
                      setPeriod('');
                      const dateValue = e.target.value ? `${e.target.value}T00:00:00` : '';
                      updateFilters({
                        ...filters,
                        startDate: dateValue,
                        page: 1
                      });
                    }}
                    className={getFilterClasses(filters.startDate)}
                  />
                </div>

                {/* End Date */}
                <div>
                  <label className="form-label">End Date</label>
                  <input
                    type="date"
                    value={filters.endDate ? filters.endDate.split('T')[0] : ''}
                    onChange={(e) => {
                      // Clear period selection when manually changing dates
                      setPeriod('');
                      const dateValue = e.target.value ? `${e.target.value}T23:59:59` : '';
                      updateFilters({
                        ...filters,
                        endDate: dateValue,
                        page: 1
                      });
                    }}
                    className={getFilterClasses(filters.endDate)}
                  />
                </div>

                {/* Quick Date Filters */}
                <div>
                  <label className="form-label">Time Period</label>
                  <div className="flex flex-wrap gap-2">
                    {['today', 'yesterday', 'thisWeek', 'pastWeek', 'thisMonth'].map((p) => (
                      <button
                        key={p}
                        type="button"
                        onClick={() => handlePeriodChange(p)}
                        className={`px-3 py-1 text-xs font-medium rounded-md border transition-colors ${period === p
                          ? 'bg-blue-600 text-white border-blue-600 shadow-md ring-1 ring-blue-200'
                          : 'bg-white text-gray-700 border-gray-300 hover:bg-blue-50 hover:border-blue-300'
                          }`}
                      >
                        {p === 'today' && 'Today'}
                        {p === 'yesterday' && 'Yesterday'}
                        {p === 'thisWeek' && 'This Week'}
                        {p === 'pastWeek' && 'Past Week'}
                        {p === 'thisMonth' && 'This Month'}
                      </button>
                    ))}
                    <button
                      type="button"
                      onClick={() => {
                        setPeriod('');
                        updateFilters({
                          ...filters,
                          startDate: '',
                          endDate: '',
                          page: 1
                        });
                      }}
                      className="px-3 py-1 text-xs font-medium rounded-md border bg-gray-100 text-gray-700 border-gray-300 hover:bg-gray-200"
                    >
                      Clear Dates
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Trip Grouping Toggle */}
            <div className="p-4 bg-gray-50 rounded-md space-y-3 mt-5 justify-between items-center">
              <label className="form-label">Time Period</label>
              <div className="flex justify-between items-center">

                {/* 1. Passenger + Time grouping */}
                <div className="items-center justify-between">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="groupTrips"
                      checked={filters.groupTrips}
                      onChange={(e) => handleFilterChange('groupTrips', e.target.checked)}
                      disabled={filters.status === 'COMPLETED' || filters.status === 'FINISHED'}
                      className={`${getFilterClasses(filters.groupTrips, true)} ${(filters.status === 'COMPLETED' || filters.status === 'FINISHED') ? 'opacity-50 cursor-not-allowed' : ''
                        }`}
                      title={(filters.status === 'COMPLETED' || filters.status === 'FINISHED') ? 'Grouping is disabled for completed trips' : ''}
                    />
                    <label
                      htmlFor="groupTrips"
                      className={`ml-2 text-sm ${filters.status === 'COMPLETED' ? 'text-gray-400' : 'text-gray-700'
                        }`}
                    >
                      Group by Passenger (30-minute window)
                    </label>
                  </div>
                  <span className="text-xs text-gray-500">
                    Groups multiple booking attempts by the same passenger within 30 minutes
                  </span>
                </div>

                {/* 2. Failed trips + drop radius */}
                {/* <div className="items-center justify-between">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="groupFailedByDropRadius"
                      checked={filters.groupFailedByDropRadius}
                      disabled={filters.status === 'COMPLETED' || filters.status === 'FINISHED'}
                      onChange={(e) =>
                        handleFilterChange('groupFailedByDropRadius', e.target.checked)
                      }
                      className={getFilterClasses(filters.groupFailedByDropRadius, true)}
                    />
                    <label htmlFor="groupFailedByDropRadius" className="ml-2 text-sm text-gray-700">
                      Group Failed Trips by Drop Location (500m)
                    </label>
                  </div>
                  <span className="text-xs text-gray-500">
                    Groups failed trips with nearby drop locations
                  </span>
                </div> */}

                {/* 3. Pending trips + drop radius */}
                {/* <div className="items-center justify-between">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="groupPendingByDropRadius"
                      checked={filters.groupPendingByDropRadius}
                      disabled={filters.status === 'COMPLETED' || filters.status === 'FINISHED'}
                      onChange={(e) =>
                        handleFilterChange('groupPendingByDropRadius', e.target.checked)
                      }
                      className={getFilterClasses(filters.groupPendingByDropRadius, true)}
                    />
                    <label htmlFor="groupPendingByDropRadius" className="ml-2 text-sm text-gray-700">
                      Group Pending Trips by Drop Location (500m)
                    </label>
                  </div>
                  <span className="text-xs text-gray-500">
                    Groups pending trips with nearby drop locations
                  </span>
                </div> */}
              </div>
            </div>

            <div className="flex gap-6">
              <div>
                <label className="form-label">Verification Filter</label>
                <div className="flex items-center mt-2">
                  <input
                    type="checkbox"
                    id="verificationNeeded"
                    checked={filters.verificationNeeded}
                    onChange={(e) => handleFilterChange('verificationNeeded', e.target.checked)}
                    className={getFilterClasses(filters.verificationNeeded, true)}
                  />
                  <label htmlFor="verificationNeeded" className="ml-2 text-sm font-medium text-gray-700">
                    Needed Verification
                  </label>
                </div>
              </div>

              <div>
                <label className="form-label">Cancel Reason</label>
                <div className="flex items-center mt-2">
                  <input
                    type="checkbox"
                    id="showCancelReason"
                    checked={filters.showCancelReason}
                    onChange={(e) => handleFilterChange('showCancelReason', e.target.checked)}
                    className={getFilterClasses(filters.showCancelReason, true)}
                  />
                  <label htmlFor="showCancelReason" className="ml-2 text-sm font-medium text-gray-700">
                    Show Cancel Reason
                  </label>
                </div>
              </div>
            </div>

          </div>

          {/* Expand/Collapse Button - Fixed to bottom right of card */}
          <div className="absolute bottom-2 right-4">
            <button
              onClick={() => setFiltersExpanded(!filtersExpanded)}
              className="flex items-center gap-2 px-4 text-sm font-medium text-gray-700 rounded-full hover:text-blue-600"
            >
              <span className="transition-all duration-200">
                {filtersExpanded ? 'Collapse' : 'Expand Filters'}
              </span>
              <div className={`transition-transform duration-300 ease-in-out ${filtersExpanded ? 'rotate-180' : 'rotate-0'
                }`}>
                <ChevronDown className="h-4 w-4" />
              </div>
            </button>
          </div>
        </div>
      </div>

      {/* Trips Table */}
      <div className="card">
        <div className="card-header">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium text-gray-900">
              Trips ({pagination.totalRecords || 0})
            </h3>
            <button
              onClick={handleExportToExcel}
              className="btn btn-outline"
              disabled={isLoading || isFetching || trips.length === 0}
            >
              <Download className="h-4 w-4 mr-2" />
              Export
            </button>
          </div>
        </div>
        <div className="card-body">
          {(isLoading || isFetching) ? (
            <div className="flex items-center justify-center py-8">
              <div className="loading-spinner"></div>
            </div>
          ) : trips.length === 0 ? (
            <div className="empty-state">
              <Car className="empty-state-icon" />
              <h3 className="empty-state-title">No trips found</h3>
              <p className="empty-state-description">
                No trips match your current filters.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="table min-w-[1200px]">
                <thead className="table-header">
                  <tr>
                    <th className="table-header-cell">Trip ID</th>
                    <th className="table-header-cell">Passenger</th>
                    <th className="table-header-cell">Driver</th>
                    <th className="table-header-cell">Route</th>
                    <th className="table-header-cell">Status</th>
                    <th className="table-header-cell">Payment</th>
                    <th className="table-header-cell">Fare</th>
                    {filters.groupTrips && (
                      <th className="table-header-cell">Attempts</th>
                    )}
                    <th className="table-header-cell">Created</th>
                    <th className="table-header-cell">Actions</th>
                  </tr>
                </thead>
                <tbody className="table-body">
                  {trips.map((trip) => (
                    <React.Fragment key={trip._id}>
                      <tr className={`table-row ${filters.verificationNeeded && tripNeedsVerification(trip) ? 'bg-red-50 border-l-4 border-red-300' : ''}`}>
                        <td className="table-cell">
                          <div className="text-sm font-medium text-gray-900">
                            {trip.rideId}
                          </div>
                          {trip.otp &&
                            <div className="text-sm text-gray-500">
                              OTP: {trip.otp}
                            </div>}
                        </td>
                        <td className="table-cell">
                          <div className="text-sm text-gray-900">
                            {trip.bookingForName || 'N/A'}
                          </div>
                          <div className="text-sm text-gray-500">
                            {trip.bookingForPhone || 'N/A'}
                          </div>
                        </td>
                        <td className="table-cell">
                          {trip.driverId ? (
                            <div className="text-sm text-gray-900">
                              {trip.driverId.name}
                            </div>
                          ) : (
                            <span className="text-sm text-gray-500">Not assigned</span>
                          )}
                        </td>
                        <td className="table-cell">
                          <TripLocationViewer
                            key={trip._id}
                            tripId={trip._id}
                            startLocation={trip.startLocation}
                            endLocation={trip.endLocation}
                          />
                        </td>
                        <td className="table-cell">
                          {getStatusBadge(trip)}
                          {filters.showCancelReason && trip.cancelReason &&
                            !['COMPLETED', 'DIVERGED'].includes(trip?.status) && (
                              <div className="mt-2 p-2 bg-red-50 border border-red-100 rounded-md space-y-1">
                                <div className="flex items-center gap-1">
                                  <span className="text-xs font-semibold text-red-400 uppercase tracking-wide">By:</span>
                                  <span className="text-xs text-red-700 font-medium">{cancelReasons(trip?.cancelledBy)}</span>
                                </div>
                                <div className="flex items-start gap-1">
                                  <span className="text-xs font-semibold text-red-400 uppercase tracking-wide shrink-0">Reason:</span>
                                  <span className="text-xs text-red-700">{cancelReasons(trip?.cancelReason)}</span>
                                </div>
                              </div>
                          )}
                        </td>
                        <td className="table-cell">
                          {getPaymentMethodBadge(trip.paymentMethod)}
                        </td>
                        <td className="table-cell">
                          <div className="text-sm font-medium text-gray-900">
                            {trip.actualFare ? (
                              `₹${trip.actualFare.toFixed(2)}`
                            ) : (
                              <span className="text-gray-400">₹{calculateFare(trip).toFixed(2)} (Est.)</span>
                            )}
                          </div>
                          {trip.finalDistance && (
                            <div className="text-sm text-gray-500 mt-1">
                              {Math.floor(trip.finalDistance * 10) / 10} km
                            </div>
                          )}
                        </td>
                        {filters.groupTrips && (
                          <td className="table-cell">
                            <div className="flex items-center space-x-2">
                              {trip.attempts > 1 ? (
                                <button
                                  onClick={() => toggleGroupExpansion(trip._id)}
                                  className="flex items-center space-x-1 px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded-full hover:bg-blue-200"
                                >
                                  <span>{trip.attempts} attempts</span>
                                  <ChevronDown className={`h-3 w-3 transform transition-transform ${expandedGroups.has(trip._id) ? 'rotate-180' : ''
                                    }`} />
                                </button>
                              ) : (
                                <span className="text-xs text-gray-500">1 attempt</span>
                              )}
                            </div>
                          </td>
                        )}
                        <td className="table-cell">
                          <div className="text-sm text-gray-900">
                            {trip.bookingTime ? new Date(trip.bookingTime).toLocaleDateString() : 'N/A'}
                          </div>
                          <div className="text-sm text-gray-500">
                            {trip.bookingTime ? new Date(trip.bookingTime).toLocaleTimeString() : 'N/A'}
                          </div>
                        </td>
                        <td className="table-cell">
                          <div className="flex items-center space-x-2">
                            <Link
                              to={`/trips/${trip._id}`}
                              className="text-primary-600 hover:text-primary-900"
                            >
                              <Eye className="h-4 w-4" />
                            </Link>
                            <button
                              type="button"
                              onClick={() => setRideMatchTrip({ id: trip._id, rideId: trip.rideId })}
                              title="Ride match — who was offered this trip"
                              className="text-gray-500 hover:text-primary-600"
                            >
                              <Users className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>

                      {/* Expanded Group Rows */}
                      {filters.groupTrips && trip.attempts > 1 && expandedGroups.has(trip._id) && (
                        trip.groupedTrips?.map((groupedTrip, index) => (
                          <tr key={`${trip._id}-${index}`} className={`${filters.verificationNeeded && tripNeedsVerification(groupedTrip) ? 'bg-red-50 border-l-4 border-red-300' : 'bg-gray-50 border-l-4 border-blue-300'}`}>
                            <td className="table-cell pl-8">
                              <div className="text-xs font-medium text-gray-600">
                                #{index + 1}: {groupedTrip.rideId}
                              </div>
                              {trip.otp &&
                                <div className="text-sm text-gray-500">
                                  OTP: {trip.otp}
                                </div>}
                            </td>
                            <td className="table-cell">
                              <div className="text-xs text-gray-600">
                                Same passenger
                              </div>
                            </td>
                            <td className="table-cell">
                              {groupedTrip.driverId ? (
                                <div className="text-xs text-gray-600">
                                  {groupedTrip.driverId.name}
                                </div>
                              ) : (
                                <span className="text-xs text-gray-500">Not assigned</span>
                              )}
                            </td>
                            <td className="table-cell">
                              <div className="text-xs text-gray-600">
                                Same route
                              </div>
                            </td>
                            <td className="table-cell">
                              {getStatusBadge(groupedTrip)}
                            </td>
                            <td className="table-cell">
                              {getPaymentMethodBadge(groupedTrip.paymentMethod)}
                            </td>
                            <td className="table-cell">
                              <div className="text-xs font-medium text-gray-600">
                                {groupedTrip.actualFare ? (
                                  `₹${groupedTrip.actualFare.toFixed(2)}`
                                ) : (
                                  <span className="text-gray-400">₹{calculateFare(groupedTrip).toFixed(2)} (Est.)</span>
                                )}
                              </div>
                            </td>
                            {filters.groupTrips && (
                              <td className="table-cell"></td>
                            )}
                            <td className="table-cell">
                              <div className="text-xs text-gray-600">
                                {groupedTrip.bookingTime ? new Date(groupedTrip.bookingTime).toLocaleTimeString([], {
                                  hour: '2-digit',
                                  minute: '2-digit'
                                }) : 'N/A'}
                              </div>
                            </td>
                            <td className="table-cell">
                              <Link
                                to={`/trips/${groupedTrip._id}`}
                                className="text-primary-400 hover:text-primary-600"
                                title="View Details"
                              >
                                <Eye className="h-3 w-3" />
                              </Link>
                            </td>
                          </tr>
                        ))
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Pagination */}
      {trips.length > 0 && (
        <div className="flex items-center justify-between">
          <div className="text-sm text-gray-700">
            Showing {((pagination.current - 1) * filters.limit) + 1} to{' '}
            {Math.min(((pagination.current - 1) * filters.limit) + trips.length, pagination.totalRecords)} of{' '}
            {pagination.totalRecords} results
          </div>
          {pagination.total > 1 && (
            <div className="flex space-x-2">
              <button
                onClick={() => handlePageChange(pagination.current - 1)}
                disabled={pagination.current === 1}
                className="btn btn-outline btn-sm"
              >
                Previous
              </button>
              <button
                onClick={() => handlePageChange(pagination.current + 1)}
                disabled={pagination.current === pagination.total}
                className="btn btn-outline btn-sm"
              >
                Next
              </button>
            </div>
          )}
        </div>
      )}
      {rideMatchTrip && (
        <RideMatchModal
          tripId={rideMatchTrip.id}
          rideId={rideMatchTrip.rideId}
          onClose={() => setRideMatchTrip(null)}
        />
      )}
    </div>
  );
};

export default Trips; 